import { useState } from 'react';

import { Button, Icon, Stack, Text } from '@eluve/components';

import { ToolInvocation } from './ToolCallStatus';

interface CustomInstructionSuggestionCardProps {
  toolInvocation: ToolInvocation;
  onConfirm: (message: string) => void;
  onDismiss: (message: string) => void;
}

interface InstructionSuggestion {
  instruction: string;
  reason: string;
}

function parseSuggestion(
  toolInvocation: ToolInvocation,
): InstructionSuggestion | null {
  const data =
    toolInvocation.state === 'output-available'
      ? toolInvocation.output
      : toolInvocation.input;

  if (!data || typeof data !== 'object') return null;

  const { instruction, reason } = data as Record<string, unknown>;
  if (typeof instruction !== 'string' || typeof reason !== 'string') {
    return null;
  }

  return { instruction, reason };
}

export const CustomInstructionSuggestionCard = ({
  toolInvocation,
  onConfirm,
  onDismiss,
}: CustomInstructionSuggestionCardProps) => {
  const [acted, setActed] = useState(false);
  const isComplete = toolInvocation.state === 'output-available';

  if (!isComplete) {
    return (
      <Stack
        className="w-fit rounded-full border border-border-insights-subtle bg-bg-insights-subtle px-3 py-1.5"
        inline
        align="center"
        gap={2}
        fullWidth={false}
      >
        <Icon
          name="WandSparkles"
          className="animate-pulse text-text-insights"
        />
        <Text size="xs">Preparing suggestion…</Text>
      </Stack>
    );
  }

  const suggestion = parseSuggestion(toolInvocation);

  if (!suggestion) {
    return null;
  }

  return (
    <Stack
      gap={3}
      className="rounded-lg border border-border-primary bg-bg-secondary p-4"
    >
      <Stack gap={2} align="center" direction="horizontal">
        <Icon name="Settings" size="lg" className="text-text-tertiary" />
        <Text inline size="xs" weight="semibold" color="secondary">
          Suggested Custom Instruction
        </Text>
      </Stack>

      <Stack
        gap={1}
        className="rounded-md border border-border-secondary bg-bg-primary p-3"
      >
        <Text size="xs" weight="semibold">
          {suggestion.instruction}
        </Text>
        <Text size="xs" color="tertiary" className="mt-1 italic">
          {suggestion.reason}
        </Text>
      </Stack>

      <Stack gap={2} direction="horizontal" justify="end">
        <Button
          variant="outline"
          size="sm"
          disabled={acted}
          onClick={() => {
            setActed(true);
            onDismiss?.("No thanks, don't save that as a custom instruction.");
          }}
        >
          Dismiss
        </Button>
        <Button
          size="sm"
          disabled={acted}
          onClick={() => {
            setActed(true);
            onConfirm?.('Yes, save that as a custom instruction.');
          }}
        >
          <Icon name="Save" size="auto" />
          Save Instruction
        </Button>
      </Stack>
    </Stack>
  );
};
